import React from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";

interface MarkdownWithImagesProps {
    text: string;
    images: { key: string; value: string }[];
}

const markdownComponents: Components = {
    pre: ({ children }) => (
        <pre
            style={{
                background: "#f4f4f4",
                padding: "12px",
                borderRadius: "8px",
                overflowX: "auto",
                fontFamily:
                    "\"Segoe UI\", \"Segoe UI Web (West European)\", \"Segoe UI\", -apple-system, BlinkMacSystemFont, Roboto, \"Helvetica Neue\", sans-serif",
            }}
        >
            {children}
        </pre>
    ),
    code: ({ className, children }) => (
        <code className={className} style={{ fontFamily: "inherit" }}>
            {children}
        </code>
    ),
    a: ({ href, children, ...props }: any) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
        >
            {children}
        </a>
    ),
};

const MarkdownWithImages: React.FC<MarkdownWithImagesProps> = ({ text, images }) => {
    if (!images || images.length === 0) return <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>;

    // Create a regex to match all keys in text
    const keysPattern = images.map(img => img.key).join("|");
    const regex = new RegExp(`(${keysPattern})`, "g");

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    text.replace(regex, (matchedKey, _, offset) => {
        // Add text before the image
        if (offset > lastIndex) {
            parts.push(text.slice(lastIndex, offset));
        }

        // Find the corresponding image
        const imageItem = images.find(img => img.key === matchedKey);
        if (imageItem) {
            parts.push(
                <div key={offset} style={{ margin: "8px 0" }}>
                    <img
                        src={`data:image/webp;base64,${imageItem.value}`}
                        alt={matchedKey}
                        style={{ maxWidth: "100%", borderRadius: "8px" }}
                    />
                </div>
            );
        }

        lastIndex = offset + matchedKey.length;
        return matchedKey;
    });

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    // Render all parts: text parts as Markdown, images as React elements
    return (
        <div style={{ fontSize: "14px" }}>
            {parts.map((part, index) =>
                typeof part === "string" ? (
                    <ReactMarkdown
                        key={index}
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw, rehypeHighlight]}
                        components={markdownComponents}
                        skipHtml={false}
                    >
                        {part}
                    </ReactMarkdown>
                ) : (
                    part
                )
            )}
        </div>
    );
};

export default MarkdownWithImages;
